import {
  ActionIcon,
  Button,
  Center,
  Flex,
  Grid,
  Group,
  Image,
  Box,
  Stack,
  Text,
} from "@mantine/core";
import {
  Dropzone,
  IMAGE_MIME_TYPE,
  PDF_MIME_TYPE,
  MS_WORD_MIME_TYPE,
  MS_EXCEL_MIME_TYPE,
} from "@mantine/dropzone";
import { DatePicker } from "@mantine/dates";
import { useEffect, useRef, useState } from "react";
import { BiDownload, BiTrash } from "react-icons/bi";
import variables from "styles/variables.module.scss";
import { LocalStorageKey } from "@/services/constants";
import { IconX } from "@tabler/icons-react";
import { getDescriptionStyleJSX } from "../nonPrimitiveType/formDynamicInput";
import { getPortalApiBase, purfifyHTML } from "@/services/services";
import parse from "html-react-parser";
import { isNil } from "lodash";
import useTrans from "@/hooks/useTrans";
import { useRouter } from "next/router";
import { generateToken } from "services/otplibServices";
import styles from "../../styles/dynamic-form.module.scss";
import moment from "moment";

/**
 * Format bytes as human-readable text.
 *
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 *
 * @return Formatted string.
 */
function humanFileSize(bytes, si = false, dp = 1) {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + " B";
  }

  const units = si
    ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
    : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (
    Math.round(Math.abs(bytes) * r) / r >= thresh &&
    u < units.length - 1
  );

  return bytes.toFixed(dp) + " " + units[u];
}

/**
 * Returns a full URL for a file given either a full URL or a bare filename/ID.
 * Old backend returned full https://... URLs; new backend returns just the filename.
 */
function appendDynamicToken(url) {
  if (!url) return "";

  const hashIndex = url.indexOf("#");
  const urlWithoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : "";
  const queryIndex = urlWithoutHash.indexOf("?");
  const baseUrl =
    queryIndex >= 0 ? urlWithoutHash.slice(0, queryIndex) : urlWithoutHash;
  const queryString =
    queryIndex >= 0 ? urlWithoutHash.slice(queryIndex + 1) : "";
  const queryParams = new URLSearchParams(queryString);

  queryParams.set("dynamic_token", generateToken());

  return `${baseUrl}?${queryParams.toString()}${hash}`;
}

function buildFileUrl(urlOrFilename, actualHostName) {
  if (!urlOrFilename) return "";
  if (/^(blob:|data:)/i.test(urlOrFilename)) return urlOrFilename;

  const fileUrl = /^https?:\/\//i.test(urlOrFilename)
    ? urlOrFilename
    : `${actualHostName}/portals/file-manager/${urlOrFilename}`;

  return appendDynamicToken(fileUrl);
}

// Default seed avatars assigned by the backend (e.g. "/assets/avatars/2-5.png")
// are not real uploads — treat them as empty so the upload placeholder shows.
function isDefaultAvatarPath(value) {
  const path =
    typeof value === "string" ? value : value?.url || value?.name || "";
  return /\/assets\/avatars\//i.test(path);
}

const imageFileExtensionPattern =
  /\.(png|jpe?g|webp|gif|svg|bmp|tiff?)(?:[?#].*)?$/i;
const localPreviewUrlMap = new WeakMap();

function isDraggingFiles(event) {
  return Array.from(event.dataTransfer?.types || []).includes("Files");
}

function isBrowserFile(file) {
  return typeof File !== "undefined" && file instanceof File;
}

function getLocalPreviewUrl(file) {
  if (!isBrowserFile(file)) return "";
  const cachedUrl = localPreviewUrlMap.get(file);

  if (cachedUrl) return cachedUrl;

  const previewUrl = URL.createObjectURL(file);
  localPreviewUrlMap.set(file, previewUrl);
  return previewUrl;
}

function isImagePreviewFile(file) {
  if (!file) return false;
  if (file.type?.startsWith("image/")) return true;

  const filename = file.originalName || file.name || file.url || "";
  return imageFileExtensionPattern.test(filename);
}

function getPreviewSource(file, actualHostName) {
  if (!file || !isImagePreviewFile(file)) return "";

  const localPreviewUrl = getLocalPreviewUrl(file);
  if (localPreviewUrl) return localPreviewUrl;

  return buildFileUrl(
    file.url || file.name || file.originalName,
    actualHostName,
  );
}

function getPositiveInteger(value, fallback) {
  return Number.isInteger(value) && value > 0 ? value : fallback;
}

const uploadButtonStyles = {
  root: {
    backgroundColor: variables.primaryColor,
    borderColor: variables.primaryColor,
    "&:hover": {
      backgroundColor: variables.primaryColor,
      borderColor: variables.primaryColor,
    },
  },
  label: { pointerEvents: "none" },
};

function FileThumbnail({ file, actualHostName, width = 96, height = 64 }) {
  const src = getPreviewSource(file, actualHostName);

  if (!src) return null;

  return (
    <Image
      src={src}
      alt={file.originalName || file.name || ""}
      width={width}
      height={height}
      radius={6}
      fit="contain"
      withPlaceholder
      styles={{
        root: {
          flexShrink: 0,
          backgroundColor: "#f8fafc",
          border: "1px solid #e9ecef",
          borderRadius: 6,
        },
        image: { objectFit: "contain" },
      }}
    />
  );
}

function usePreventWindowFileDrop() {
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const preventFileDrop = (event) => {
      if (!isDraggingFiles(event)) return;
      event.preventDefault();
    };

    window.addEventListener("dragover", preventFileDrop);
    window.addEventListener("drop", preventFileDrop);

    return () => {
      window.removeEventListener("dragover", preventFileDrop);
      window.removeEventListener("drop", preventFileDrop);
    };
  }, []);
}

function getFileRejections({
  file,
  acceptedFileType,
  maxSize,
  maxFiles,
  fileIndex = 0,
}) {
  const errors = [];

  const acceptedList = Array.isArray(acceptedFileType) ? acceptedFileType : [];
  const isTypeAccepted =
    acceptedList.length === 0 ||
    acceptedList.some((accepted) => {
      if (!accepted) return false;
      if (accepted.startsWith(".")) {
        return (file.name || "").toLowerCase().endsWith(accepted.toLowerCase());
      }
      if (accepted.endsWith("/*")) {
        return (file.type || "").startsWith(accepted.slice(0, -1));
      }
      return file.type === accepted;
    });

  if (!isTypeAccepted) errors.push({ code: "file-invalid-type" });
  if (maxSize && file.size > maxSize) errors.push({ code: "file-too-large" });
  if (maxFiles && fileIndex >= maxFiles) errors.push({ code: "too-many-files" });

  return errors;
}

/**
 * Renders a dynamic input dropzone based on the input template type.
 * @param {object} props - The props containing inputTemplate type.
 * @returns {JSX.Element} - The rendered input dropzone.
 */
function FormDynamicInputDropzone(props) {
  usePreventWindowFileDrop();

  // Initialize eleHTML as a fragment
  let eleHTML = <></>;

  // Render different dropzone components based on inputTemplate type
  switch (props.inputTemplate.type) {
    case "drop-zone":
      eleHTML = <ImageDropzone {...props} />;
      break;

    case "file-picker":
      eleHTML = <FileDropzone {...props} />;
      break;

    case "file-picker-many":
      eleHTML = <FileManyDropzone {...props} />;
      break;
    case "generic-file-picker":
      eleHTML = <GenericDropzone {...props} />;
      break;
    case "shnbnk-file-picker":
      eleHTML = <SHNBNKDropzoneAndDate {...props} />;
      break;
    default:
      break;
  }

  return eleHTML;
}

function ImageDropzone(props) {
  const zoneWidth = 180;
  const [error, setError] = useState(null);
  const [maxSize, setMaxSize] = useState(5 * 1024 ** 2);
  const [imageFile, setImageFile] = useState(null);
  const [oldInvisible, setOldInvisible] = useState(false);

  const trans = useTrans();
  const inputProps = { ...props.formInputProps };
  const inputTemplate = { ...props.inputTemplate };
  const disabled = props.disabledInput || inputProps.disabled;

  const actualHostName = getPortalApiBase(props.hostNameUrl);

  function getFilename(pathOrUrl) {
    if (/^https?:\/\//i.test(pathOrUrl)) {
      const match = pathOrUrl.match(
        /^https?:\/\/[^\s?#]+\.(png|jpg|jpeg|gif|webp|svg)(?:[?#].*)?$/i,
      );
      return match ? match[1] : "";
    }
    // Plain filename/ID — extract extension directly
    const match = pathOrUrl.match(/\.(png|jpg|jpeg|gif|webp|svg|bmp|tiff)$/i);
    return match ? match[1] : "";
  }

  function getImageMime(filename) {
    if (!filename) return "application/octet-stream";
    const ext = filename?.split(".").pop()?.toLowerCase();
    const map = {
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      webp: "image/webp",
      svg: "image/svg+xml",
      bmp: "image/bmp",
      tiff: "image/tiff",
    };
    return ext && map[ext] ? map[ext] : "application/octet-stream";
  }

  inputProps.onDrop = (files) => {
    if (!files || !Array.isArray(files) || files.length <= 0) return;
    setError(null);

    const file = files[0];
    if (!file.type?.startsWith("image/")) {
      handleReject([{ file, errors: [{ code: "file-invalid-type" }] }]);
      return;
    }

    const url = URL.createObjectURL(file);
    const name = file.name;
    const type = file.type;

    setImageFile({ name, type, url });
    props.formInputProps.onChange(file);
    const index =
      props.fileToUploadArray && props.setFileToUploadArray
        ? props.fileToUploadArray.findIndex(
            (e) => e.inputName === props.inputName,
          )
        : null;
    if (index !== null) {
      let copyFileToUploadArray;
      if (index >= 0) {
        copyFileToUploadArray = [
          ...props.fileToUploadArray.slice(0, index),
          ...props.fileToUploadArray.slice(index + 1),
        ];
      } else {
        copyFileToUploadArray = [...props.fileToUploadArray];
      }
      copyFileToUploadArray.push({
        inputName: props.inputName,
        file: file,
        inputTemplate: props.inputTemplate,
      });
      props.setFileToUploadArray(copyFileToUploadArray);
    }
  };

  useEffect(() => {
    if (
      isNil(props.inputTemplate?.maxSize) ||
      typeof props.inputTemplate?.maxSize != "number"
    )
      return;
    setMaxSize(props.inputTemplate?.maxSize);
  }, [props.inputTemplate?.maxSize]);

  useEffect(() => {
    if (isDefaultAvatarPath(props.initialValue)) {
      setImageFile(null);
      props.formInputProps.onChange(null);
      return;
    }
    if (typeof props.initialValue === "string") {
      if (props.initialValue === "") {
        setImageFile(null);
      } else {
        const filename = getFilename(props.initialValue);
        if (filename !== "") {
          let objTemp = {
            name: filename,
            type: getImageMime(filename),
            url: buildFileUrl(props.initialValue, actualHostName),
          };
          setImageFile(objTemp);
          props.formInputProps.onChange(objTemp);
        } else {
          setImageFile(null);
          props.formInputProps.onChange(null);
        }
        return;
      }
      return;
    }
    setImageFile(props.initialValue);
  }, [props.initialValue]);

  useEffect(() => {
    if (props.inputTemplate.invisible) {
      setOldInvisible(true);
      props.formInputProps.onChange(undefined);
    } else if (oldInvisible) {
      setOldInvisible(false);
      props.formInputProps.onChange(
        props.initialValue === undefined ? null : props.initialValue,
      );
    }
  }, [props.inputTemplate.invisible]);

  const handleReject = (rejections) => {
    const firstError = rejections[0]?.errors[0]?.code;

    switch (firstError) {
      case "file-too-large":
        setError(
          `${props.inputTemplate?.errorSizeMsg || trans.formDynamic.file.errorSize} ${humanFileSize(maxSize, true, 0)}`,
        );
        break;

      case "file-invalid-type":
        setError(
          `${props.inputTemplate?.errorTypeMsg || trans.formDynamic.file.errorType}`,
        );
        break;

      case "too-many-files":
        setError(
          `${props.inputTemplate?.errorTooManyMsg || trans.formDynamic.file.errorType}`,
        );
        break;

      default:
        setError(
          `${props.inputTemplate?.errorGenericMsg || trans.formDynamic.file.errorGeneric}`,
        );
    }
  };

  const uploadImageJSX = (
    <Stack spacing={25} pt={25} pl={25} pr={25} pb={34} mt={15}>
      <Center>
        <Image width={32} alt={""} src={"/images/cloud-upload.svg"} />
      </Center>
      <Text ta="center" fz={16} c={"rgba(21, 20, 57, 0.8)"}>
        Kéo thả hình ảnh ở đây{" "}
        {props.requiredInput && <span style={{ color: "red" }}>{" *"}</span>}
      </Text>
    </Stack>
  );
  const previewImageJSX = () => {
    if (!imageFile) return <></>;
    return (
      <Image
        pl={0}
        pt={8}
        radius={10}
        width={zoneWidth - 20}
        height={zoneWidth - 20}
        src={buildFileUrl(imageFile.url || imageFile.name, actualHostName)}
        alt={imageFile.name}
      />
    );
  };

  const closeIconJSX = (
    <ActionIcon
      color="red"
      radius="xl"
      size="lg"
      variant="filled"
      style={{
        position: "absolute",
        zIndex: 10,
        top: `-10px`,
        right: `calc( 0.5 * (100% - ${zoneWidth}px) - 10px )`,
      }}
      onClick={() => {
        setImageFile(null);
        props.formInputProps.onChange("");
        props.setFileToUploadArray([]);
      }}
    >
      <IconX size={18} />
    </ActionIcon>
  );

  return (
    <Center
      style={{ marginBottom: 20, position: "relative" }}
      data-form-error={inputProps.error ? "true" : undefined}
    >
      {/* {((imageFile && !props.disabledInput) || inputProps.disabled) &&
        closeIconJSX} */}
      <Stack>
        <Center>
          <Dropzone
            accept={IMAGE_MIME_TYPE}
            disabled={disabled}
            multiple={false}
            maxSize={maxSize}
            {...inputProps}
            style={{
              width: zoneWidth,
              height: zoneWidth,
              padding: 0,
              borderRadius: 8,
              backgroundColor: disabled && "transparent",
              cursor: disabled && "not-allowed",
              border: !isNil(inputProps.error) && "2px dashed red",
            }}
            onReject={handleReject}
          >
            <Center>{imageFile ? previewImageJSX() : uploadImageJSX}</Center>
          </Dropzone>
        </Center>
        {inputProps.error ? (
          <div
            style={{
              marginTop: -5,
              marginBottom: 20,
              color: "red",
              fontSize: 12,
            }}
          >
            {inputProps.error}
          </div>
        ) : (
          <></>
        )}
        {error && (
          <div
            style={{
              marginBottom: 10,
              color: "red",
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}
        {inputTemplate?.description && (
          <>{parse(purfifyHTML(inputTemplate?.description))}</>
        )}
      </Stack>
    </Center>
  );
}

function FileDropzone(props) {
  const openRef = useRef(null);
  const [error, setError] = useState(null);
  const [maxSize, setMaxSize] = useState(5 * 1024 ** 2);
  const [filePreview, setFilePreview] = useState(null);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [oldInvisible, setOldInvisible] = useState(false);
  const [showButton, setShowButton] = useState(true);
  const [acceptedFileType, setAcceptedFileType] = useState([
    ...IMAGE_MIME_TYPE,
    ...PDF_MIME_TYPE,
  ]);

  const trans = useTrans();
  const inputProps = { ...props.formInputProps };
  const disabled = props.disabledInput || inputProps.disabled;
  const actualHostName = getPortalApiBase(props.hostNameUrl);
  const descriptionJSX = getDescriptionStyleJSX(props.inputTemplate);

  useEffect(() => {
    switch (localStorage.getItem(LocalStorageKey.TENANT)) {
      case "svfc":
        setAcceptedFileType([
          ...acceptedFileType,
          ...MS_WORD_MIME_TYPE,
          ...MS_EXCEL_MIME_TYPE,
        ]);
      case "shb":
        setAcceptedFileType([
          ...PDF_MIME_TYPE,
          ...MS_WORD_MIME_TYPE,
          ...MS_EXCEL_MIME_TYPE,
        ]);
        break;
    }
  }, []);

  useEffect(() => {
    if (props.inputTemplate.showButton) {
      setShowButton(props.inputTemplate.showButton);
    }
  }, [props.inputTemplate]);

  const handleFile = (file) => {
    if (!file) return;

    setError(null);

    const url = URL.createObjectURL(file);
    const name = file.name;
    const type = file.type;

    setHasUploaded(true);
    setFilePreview({ name, type, url });
    props.formInputProps.onChange(file);

    const index =
      props.fileToUploadArray && props.setFileToUploadArray
        ? props.fileToUploadArray.findIndex(
            (e) => e.inputName === props.inputName,
          )
        : null;

    if (index !== null) {
      let copyFileToUploadArray;

      if (index >= 0) {
        copyFileToUploadArray = [
          ...props.fileToUploadArray.slice(0, index),
          ...props.fileToUploadArray.slice(index + 1),
        ];
      } else {
        copyFileToUploadArray = [...props.fileToUploadArray];
      }

      copyFileToUploadArray.push({
        inputName: props.inputName,
        file: file,
        inputTemplate: props.inputTemplate,
      });

      props.setFileToUploadArray(copyFileToUploadArray);
    }
  };

  inputProps.onDrop = (files) => {
    if (!files || !Array.isArray(files) || files.length <= 0) return;

    handleFile(files[0]);
  };

  inputProps.onChange = (event) => {
    const files = event?.target?.files;

    if (!files || files.length === 0) return;

    const file = files[0];
    const errors = getFileRejections({ file, acceptedFileType, maxSize });

    if (errors.length > 0) {
      handleReject([{ file, errors }]);
      event.target.value = "";
      return;
    }

    handleFile(file);
  };

  const handleReject = (rejections) => {
    const firstError = rejections[0]?.errors[0]?.code;

    switch (firstError) {
      case "file-too-large":
        setError(
          `${props.inputTemplate?.errorSizeMsg || trans.formDynamic.file.errorSize} ${humanFileSize(maxSize, true, 0)}`,
        );
        break;

      case "file-invalid-type":
        setError(
          `${props.inputTemplate?.errorTypeMsg || trans.formDynamic.file.errorType}`,
        );
        break;

      case "too-many-files":
        setError(
          `${props.inputTemplate?.errorTooManyMsg || trans.formDynamic.file.errorType}`,
        );
        break;

      default:
        setError(
          `${props.inputTemplate?.errorGenericMsg || trans.formDynamic.file.errorGeneric}`,
        );
    }
  };

  useEffect(() => {
    if (
      isNil(props.inputTemplate?.maxSize) ||
      typeof props.inputTemplate?.maxSize != "number"
    )
      return;
    setMaxSize(props.inputTemplate?.maxSize);
  }, [props.inputTemplate?.maxSize]);

  useEffect(() => {
    let temp = props.inputTemplate?.acceptedFileType;
    if (isNil(temp) || !Array.isArray(temp) || temp.length == 0) return;

    setAcceptedFileType(temp);
  }, [props.inputTemplate?.acceptedFileType]);

  useEffect(() => {
    if (hasUploaded) {
      setHasUploaded(false);
    }
  }, [props.isFormSubmitted]);

  useEffect(() => {
    setFilePreview(props.initialValue);
  }, [props.initialValue]);

  useEffect(() => {
    if (props.inputTemplate.invisible) {
      setOldInvisible(true);
      handleDelete(true);
    } else if (oldInvisible) {
      setOldInvisible(false);
      setFilePreview(null);
      inputProps.onChange(
        props.initialValue === undefined ? null : props.initialValue,
      );
    }
  }, [props.inputTemplate.invisible]);

  const handleDelete = (invisible = false) => {
    setFilePreview(invisible ? undefined : null);
    const index =
      props.fileToUploadArray && props.setFileToUploadArray
        ? props.fileToUploadArray.findIndex(
            (e) => e.inputName === props.inputName,
          )
        : null;

    if (index !== null && index >= 0) {
      const copyFileToUploadArray = [
        ...props.fileToUploadArray.slice(0, index),
        ...props.fileToUploadArray.slice(index + 1),
      ];
      props.setFileToUploadArray(copyFileToUploadArray);
    }
    props.formInputProps.onChange(invisible ? undefined : null);
  };

  const filePreviewJSX = filePreview ? (
    <Center w="100%">
      <Group
        spacing={10}
        align="center"
        position="center"
        noWrap
        style={{ maxWidth: "100%", overflow: "hidden" }}
      >
        <FileThumbnail file={filePreview} actualHostName={actualHostName} />
        <Text
          style={{
            minWidth: 0,
            maxWidth: "70%",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
          ta={"center"}
          fz={16}
        >
          {filePreview.originalName ||
            filePreview.name ||
            "Failed to fetch filename"}
        </Text>
        <Flex gap={10} align={"center"}>
          <ActionIcon
            color="dark"
            style={{
              cursor: hasUploaded && "not-allowed",
              color: hasUploaded && "lightgray",
              backgroundColor: hasUploaded && "transparent",
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (hasUploaded) return;
              let validFileName = filePreview.url || filePreview.name;
              window.open(
                buildFileUrl(validFileName, actualHostName),
                "_blank",
              );
            }}
          >
            <BiDownload size={"1.3rem"} />
          </ActionIcon>
          {!disabled && (
            <ActionIcon
              color="red"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleDelete();
              }}
            >
              <BiTrash size={"1.3rem"} />
            </ActionIcon>
          )}
        </Flex>
      </Group>
    </Center>
  ) : (
    // <Center>
    //   <Image width={32} alt={""} src={"/images/cloud-disabled.svg"} />
    // </Center>
    <></>
  );

  const renderDropzoneContent = () => (
    <Stack spacing={10} w={"100%"}>
      <Stack spacing={0} w={"100%"}>
        {props.inputTemplate.label ? (
          <Text fw={500} c={variables.primaryColor} lineClamp={2}>
            {props.inputTemplate.label}
            {props.requiredInput && <span style={{ color: "red" }}>{" *"}</span>}
          </Text>
        ) : (
          <></>
        )}
      {descriptionJSX && ( <Box fz={12} c={'#868e96'}>{descriptionJSX}</Box>)}
      </Stack>

      {inputProps.error ? (
        <div
          style={{
            marginTop: -5,
            marginBottom: 10,
            color: "red",
            fontSize: 12,
          }}
        >
          {inputProps.error}
        </div>
      ) : (
        <></>
      )}
      {error && (
        <div
          style={{
            marginBottom: 10,
            color: "red",
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}

      {filePreviewJSX}
      {!props.disabledInput && showButton && (
        <Group position="center">
          <Button
            styles={uploadButtonStyles}
            onClick={() => {
              openRef.current();
            }}
          >
            {props.inputTemplate.buttonLabel || "Tải hình ảnh/tải danh sách"}
          </Button>
        </Group>
      )}
    </Stack>
  );

  return (
    <Stack>
      <Center>
        <Dropzone
          w={'100%'}
          openRef={openRef}
          accept={acceptedFileType}
          {...inputProps}
          activateOnClick={false}
          disabled={disabled}
          multiple={false}
          maxSize={maxSize}
          style={{
            marginTop: 20,
            backgroundColor: disabled && "transparent",
            cursor: disabled && "not-allowed",
          }}
          onReject={handleReject}
          styles={{ inner: { pointerEvents: "all" } }}
          data-form-error={inputProps.error ? "true" : undefined}
        >
          <Dropzone.Idle>{renderDropzoneContent()}</Dropzone.Idle>
          <Dropzone.Accept>{renderDropzoneContent()}</Dropzone.Accept>
          <Dropzone.Reject>{renderDropzoneContent()}</Dropzone.Reject>
        </Dropzone>
      </Center>
      {props.inputTemplate?.description && (
        <>{parse(purfifyHTML(props.inputTemplate?.description))}</>
      )}
    </Stack>
  );
}

function FileManyDropzone(props) {
  const openRef = useRef(null);
  const [error, setError] = useState(null);
  const [maxSize, setMaxSize] = useState(5 * 1024 ** 2);
  const [maxFiles, setMaxFiles] = useState(2);
  const [filePreview, setFilePreview] = useState([]);
  const [hasUploadedIdx, setHasUploadedIdx] = useState([]);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [oldInvisible, setOldInvisible] = useState(false);
  const [showButton, setShowButton] = useState(true);
  const [acceptedFileType, setAcceptedFileType] = useState([
    ...IMAGE_MIME_TYPE,
    ...PDF_MIME_TYPE,
  ]);

  const trans = useTrans();
  const inputProps = { ...props.formInputProps };
  const disabled = props.disabledInput;
  const actualHostName = getPortalApiBase(props.hostNameUrl);
  const descriptionJSX = getDescriptionStyleJSX(props.inputTemplate);

  useEffect(() => {
    if (props.inputTemplate.showButton) {
      setShowButton(props.inputTemplate.showButton);
    }
  }, [props.inputTemplate]);

  useEffect(() => {
    switch (localStorage.getItem(LocalStorageKey.TENANT)) {
      case "svfc":
        setAcceptedFileType([
          ...acceptedFileType,
          ...MS_WORD_MIME_TYPE,
          ...MS_EXCEL_MIME_TYPE,
        ]);
      case "shb":
        setAcceptedFileType([
          ...PDF_MIME_TYPE,
          ...MS_WORD_MIME_TYPE,
          ...MS_EXCEL_MIME_TYPE,
        ]);
        break;
    }
  }, []);

  const lastFilesRef = useRef([]);

  const handleFiles = (incomingFiles) => {
    if (!incomingFiles || incomingFiles.length === 0) return;

    // 🛑 dedupe: prevent double-trigger (onDrop + onChange)
    const isSameBatch =
      lastFilesRef.current.length === incomingFiles.length &&
      lastFilesRef.current.every((f, i) => {
        const nf = incomingFiles[i];
        return (
          f.name === nf.name &&
          f.size === nf.size &&
          f.lastModified === nf.lastModified
        );
      });

    if (isSameBatch) return;

    lastFilesRef.current = incomingFiles;

    setError(null);

    const currentFiles = Array.isArray(filePreview) ? filePreview : [];
    const availableSlots = maxFiles - currentFiles.length;

    if (availableSlots <= 0) {
      setError(
        `${props.inputTemplate?.errorTooManyMsg || trans.formDynamic.file.errorTooMany} ${maxFiles}`,
      );
      return;
    }

    const acceptedIncomingFiles = incomingFiles.slice(0, availableSlots);

    if (acceptedIncomingFiles.length < incomingFiles.length) {
      setError(
        `${props.inputTemplate?.errorTooManyMsg || trans.formDynamic.file.errorTooMany} ${maxFiles}`,
      );
    }

    setHasUploaded(true);

    const filePreviewCopy = [...currentFiles, ...acceptedIncomingFiles];

    // update upload array
    if (props.fileToUploadArray && props.setFileToUploadArray) {
      const copyFileToUploadArray = [...props.fileToUploadArray];

      const masterIndex = props.fileToUploadArray.findIndex(
        (e) => e.inputName === props.inputName,
      );

      if (masterIndex >= 0) {
        copyFileToUploadArray[masterIndex].file.push(...acceptedIncomingFiles);
      } else {
        copyFileToUploadArray.push({
          inputName: props.inputName,
          file: filePreviewCopy,
          inputTemplate: props.inputTemplate,
        });
      }

      props.setFileToUploadArray(copyFileToUploadArray);
    }

    props.formInputProps.onChange(filePreviewCopy);
    setFilePreview(filePreviewCopy);
  };

  async function fetchFileObject({ url, name, type }) {
    try {
      const resolvedUrl = buildFileUrl(url, actualHostName);
      const res = await fetch(resolvedUrl);
      if (!res.ok) return null;
      const blob = await res.blob();
      return new File([blob], name, { type: blob.type || type });
    } catch {
      return null;
    }
  }

  async function convertFiles(data) {
    if (!Array.isArray(data)) {
      return [];
    }
    const results = await Promise.allSettled(
      data.map((item) => {
        if (item.url && item.name) {
          return fetchFileObject(item);
        }
        return Promise.resolve(item);
      }),
    );
    return results
      .map((r) => (r.status === "fulfilled" ? r.value : null))
      .filter(Boolean);
  }

  const handleReject = (rejections) => {
    const firstError = rejections[0]?.errors[0]?.code;

    switch (firstError) {
      case "file-too-large":
        setError(
          `${props.inputTemplate?.errorSizeMsg || trans.formDynamic.file.errorSize} ${humanFileSize(maxSize, true, 0)}`,
        );
        break;

      case "file-invalid-type":
        setError(
          `${props.inputTemplate?.errorTypeMsg || trans.formDynamic.file.errorType}`,
        );
        break;

      case "too-many-files":
        setError(
          `${props.inputTemplate?.errorTooManyMsg || trans.formDynamic.file.errorTooMany} ${maxFiles}`,
        );
        break;

      default:
        setError(
          `${props.inputTemplate?.errorGenericMsg || trans.formDynamic.file.errorGeneric}`,
        );
    }
  };

  useEffect(() => {
    console.log("error", error);
  }, [error]);

  useEffect(() => {
    if (
      isNil(props.inputTemplate?.maxSize) ||
      typeof props.inputTemplate?.maxSize != "number"
    )
      return;
    setMaxSize(props.inputTemplate?.maxSize);
  }, [props.inputTemplate?.maxSize]);

  useEffect(() => {
    setMaxFiles(getPositiveInteger(props.inputTemplate?.maxFiles, 2));
  }, [props.inputTemplate?.maxFiles]);

  useEffect(() => {
    let temp = props.inputTemplate?.acceptedFileType;
    if (isNil(temp) || !Array.isArray(temp) || temp.length == 0) return;

    setAcceptedFileType(temp);
  }, [props.inputTemplate?.acceptedFileType]);

  useEffect(() => {
    let tempFileArr = props.initialValue?.filter(
      (e) => e !== null && Object.keys(e).length !== 0,
    );

    convertFiles(tempFileArr).then((result) => {
      setFilePreview(result ?? []);
    });
  }, [props.initialValue]);

  inputProps.onDrop = (files) => {
    if (!files || !Array.isArray(files) || files.length === 0) return;

    handleFiles(files);
  };

  inputProps.onChange = (event) => {
    const files = event?.target?.files;

    if (!files || files.length === 0) return;

    const rejections = [];
    const acceptedFiles = [];

    Array.from(files).forEach((file, fileIndex) => {
      const errors = getFileRejections({ file, acceptedFileType, maxSize });

      if (errors.length > 0) rejections.push({ file, errors });
      else acceptedFiles.push(file);
    });

    if (rejections.length > 0) handleReject(rejections);
    if (acceptedFiles.length > 0) handleFiles(acceptedFiles);
    event.target.value = "";
  };

  const handleDeleteFile = (index) => {
    const filePreviewCopy = [
      ...filePreview.slice(0, index),
      ...filePreview.slice(index + 1),
    ];
    props.formInputProps.onChange(filePreviewCopy);
    setFilePreview(filePreviewCopy);

    if (props.fileToUploadArray && props.setFileToUploadArray) {
      const masterIndex = props.fileToUploadArray.findIndex(
        (e) => e.inputName === props.inputName,
      );
      // const copyFileToUploadArray = [...props.fileToUploadArray.slice(0, masterIndex + index), ...props.fileToUploadArray.slice(masterIndex + index + 1)];

      let copyFileToUploadArray = [...props.fileToUploadArray];
      if (masterIndex >= 0) {
        const copyOfFiles = [...copyFileToUploadArray[masterIndex].file];
        copyFileToUploadArray[masterIndex].file = [
          ...copyOfFiles.slice(0, index),
          ...copyOfFiles.slice(index + 1),
        ];

        if (copyFileToUploadArray[masterIndex].file.length <= 0) {
          copyFileToUploadArray = [
            ...copyFileToUploadArray.slice(0, masterIndex),
            ...copyFileToUploadArray.slice(masterIndex + 1),
          ];
        }

        props.setFileToUploadArray(copyFileToUploadArray);
      }
    }
  };

  const handleDownloadFile = (file) => {
    let validFileName = file.url || file.name;
    window.open(buildFileUrl(validFileName, actualHostName), "_blank");
  };

  const handleDeleteAll = () => {
    props.formInputProps.onChange(undefined);
    setFilePreview([]);
    const masterIndex = props.fileToUploadArray.findIndex(
      (e) => e.inputName === props.inputName,
    );
    let copyFileToUploadArray = [...props.fileToUploadArray];
    copyFileToUploadArray = [
      ...copyFileToUploadArray.slice(0, masterIndex),
      ...copyFileToUploadArray.slice(masterIndex + 1),
    ];
    props.setFileToUploadArray(copyFileToUploadArray);
  };

  useEffect(() => {
    if (props.inputTemplate.invisible) {
      setOldInvisible(true);
      handleDeleteAll();
    } else if (oldInvisible) {
      setOldInvisible(false);
      props.formInputProps.onChange(
        Array.isArray(props.initialValue) ? props.initialValue : [],
      );
    }
  }, [props.inputTemplate.invisible]);

  useEffect(() => {
    if (hasUploaded) {
      setHasUploaded(false);
    }
  }, [props.isFormSubmitted]);

  const filePreviewJSX =
    filePreview && Array.isArray(filePreview) && filePreview.length > 0 ? (
      <Center w="100%">
        <Stack spacing={3} style={{ overflow: "hidden" }}>
          {filePreview.map((filePreviewSingle, i) => {
            return (
              <Group
                key={i}
                style={{
                  width: "100%",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
                spacing={10}
                align={"center"}
                noWrap
              >
                <FileThumbnail
                  file={filePreviewSingle}
                  actualHostName={actualHostName}
                  width={72}
                  height={48}
                />
                <Text
                  key={i}
                  lineClamp={1}
                  ta={"center"}
                  fz={16}
                  style={{ minWidth: 0 }}
                >
                  {filePreviewSingle.originalName || filePreviewSingle.name}
                </Text>
                <Flex gap={10} align={"center"}>
                  <ActionIcon
                    color="dark"
                    style={{
                      cursor: hasUploaded && "not-allowed",
                      color: hasUploaded && "lightgray",
                      backgroundColor: hasUploaded && "transparent",
                    }}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (hasUploaded) return;
                      handleDownloadFile(filePreviewSingle);
                    }}
                  >
                    <BiDownload size={"1.3rem"} />
                  </ActionIcon>
                  {!disabled && (
                    <ActionIcon
                      color="red"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        handleDeleteFile(i);
                      }}
                    >
                      <BiTrash size={"1.3rem"} />
                    </ActionIcon>
                  )}
                </Flex>
              </Group>
            );
          })}
        </Stack>
      </Center>
    ) : (
      // <Center>
      //   <Image width={32} alt={""} src={"/images/cloud-disabled.svg"} />
      // </Center>
      <></>
    );

  const renderDropzoneContent = () => (
    <Stack spacing={10} w={"100%"}>
      <Stack spacing={0} w={"100%"}>
        {props.inputTemplate.label ? (
          <Text fw={500} c={variables.primaryColor} lineClamp={2}>
            {props.inputTemplate.label}
            {props.requiredInput && <span style={{ color: "red" }}>{" *"}</span>}
          </Text>
        ) : (
          <></>
        )}
      {descriptionJSX && ( <Box fz={12} c={'#868e96'}>{descriptionJSX}</Box>)}
      </Stack>
      {inputProps.error ? (
        <div
          style={{
            marginTop: -5,
            marginBottom: 20,
            color: "red",
            fontSize: 12,
          }}
        >
          {inputProps.error}
        </div>
      ) : (
        <></>
      )}
      {error && (
        <div
          style={{
            marginBottom: 10,
            color: "red",
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}

      {filePreviewJSX}
      {!props.disabledInput && showButton && (
        <Group position="center">
          <Button
            disabled={filePreview.length >= maxFiles}
            styles={uploadButtonStyles}
            onClick={() => {
              if (filePreview.length >= maxFiles) return;
              openRef.current();
            }}
          >
            Tải hình ảnh/tải danh sách
          </Button>
        </Group>
      )}
    </Stack>
  );

  return (
    <Dropzone
      openRef={openRef}
      accept={acceptedFileType}
      multiple
      maxFiles={maxFiles}
      activateOnClick={false}
      {...inputProps}
      style={{
        marginTop: 20,
        backgroundColor: disabled && "transparent",
        cursor: disabled && "not-allowed",
      }}
      maxSize={maxSize}
      styles={{
        inner: { pointerEvents: "all" },
      }}
      disabled={disabled}
      onReject={handleReject}
      data-form-error={inputProps.error ? "true" : undefined}
    >
      <Dropzone.Idle>{renderDropzoneContent()}</Dropzone.Idle>
      <Dropzone.Accept>{renderDropzoneContent()}</Dropzone.Accept>
      <Dropzone.Reject>{renderDropzoneContent()}</Dropzone.Reject>
    </Dropzone>
  );
}

function GenericDropzone(props) {
  const openRef = useRef(null);
  const [error, setError] = useState(null);
  const [maxSize, setMaxSize] = useState(5 * 1024 ** 2);
  const [filePreview, setFilePreview] = useState(null);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [oldInvisible, setOldInvisible] = useState(false);
  const [acceptedFileType, setAcceptedFileType] = useState([
    ...IMAGE_MIME_TYPE,
    ...PDF_MIME_TYPE,
  ]);

  const trans = useTrans();
  const { locale } = useRouter();
  const inputProps = { ...props.formInputProps };
  const disabled = props.disabledInput || inputProps.disabled;
  const actualHostName = getPortalApiBase(props.hostNameUrl);

  const componentStyles = props.formInputProps.styles;

  const uploadFileStyle = {
    label: {
      textAlign: "center",
      fontWeight: 700,
    },
    placeholder: {
      textAlign: "center",
    },
  };

  const handleFile = (file) => {
    if (!file) return;

    setError(null);

    const url = URL.createObjectURL(file);
    const name = file.name;
    const type = file.type;

    setHasUploaded(true);
    setFilePreview({ name, type, url });
    props.formInputProps.onChange(file);

    const index =
      props.fileToUploadArray && props.setFileToUploadArray
        ? props.fileToUploadArray.findIndex(
            (e) => e.inputName === props.inputName,
          )
        : null;

    if (index !== null) {
      let copyFileToUploadArray;

      if (index >= 0) {
        copyFileToUploadArray = [
          ...props.fileToUploadArray.slice(0, index),
          ...props.fileToUploadArray.slice(index + 1),
        ];
      } else {
        copyFileToUploadArray = [...props.fileToUploadArray];
      }

      copyFileToUploadArray.push({
        inputName: props.inputName,
        file: file,
        inputTemplate: props.inputTemplate,
      });

      props.setFileToUploadArray(copyFileToUploadArray);
    }
  };

  inputProps.onDrop = (files) => {
    if (!files || !Array.isArray(files) || files.length <= 0) return;

    handleFile(files[0]);
  };

  inputProps.onChange = (event) => {
    const files = event?.target?.files;

    if (!files || files.length === 0) return;

    const file = files[0];
    const errors = getFileRejections({ file, acceptedFileType, maxSize });

    if (errors.length > 0) {
      handleReject([{ file, errors }]);
      event.target.value = "";
      return;
    }

    handleFile(file);
  };

  const handleReject = (rejections) => {
    const firstError = rejections[0]?.errors[0]?.code;
    handleDelete();

    switch (firstError) {
      case "file-too-large":
        setError(
          `${props.inputTemplate?.errorSizeMsg?.[locale] || trans.formDynamic.file.errorSize} ${humanFileSize(maxSize, true, 0)}`,
        );
        break;

      case "file-invalid-type":
        setError(
          `${props.inputTemplate?.errorTypeMsg?.[locale] || trans.formDynamic.file.errorType}`,
        );
        break;

      case "too-many-files":
        setError(
          `${props.inputTemplate?.errorTooManyMsg?.[locale] || trans.formDynamic.file.errorTooMany}`,
        );
        break;

      default:
        setError(
          `${props.inputTemplate?.errorGenericMsg?.[locale] || trans.formDynamic.file.errorGeneric}`,
        );
    }
  };

  useEffect(() => {
    if (
      isNil(props.inputTemplate?.maxSize) ||
      typeof props.inputTemplate?.maxSize != "number"
    )
      return;
    setMaxSize(props.inputTemplate?.maxSize);
  }, [props.inputTemplate?.maxSize]);

  useEffect(() => {
    let temp = props.inputTemplate?.acceptedFileType;
    if (isNil(temp) || !Array.isArray(temp) || temp.length == 0) return;

    setAcceptedFileType(temp);
  }, [props.inputTemplate?.acceptedFileType]);

  useEffect(() => {
    if (hasUploaded) {
      setHasUploaded(false);
    }
  }, [props.isFormSubmitted]);

  useEffect(() => {
    setFilePreview(props.initialValue);
  }, [props.initialValue]);

  useEffect(() => {
    if (props.inputTemplate.invisible) {
      setOldInvisible(true);
      handleDelete(true);
    } else if (oldInvisible) {
      setOldInvisible(false);
      setFilePreview(null);
      inputProps.onChange(
        props.initialValue === undefined ? null : props.initialValue,
      );
    }
  }, [props.inputTemplate.invisible]);

  const handleDelete = (invisible = false) => {
    setFilePreview(invisible ? undefined : null);
    const index =
      props.fileToUploadArray && props.setFileToUploadArray
        ? props.fileToUploadArray.findIndex(
            (e) => e.inputName === props.inputName,
          )
        : null;

    if (index !== null && index >= 0) {
      const copyFileToUploadArray = [
        ...props.fileToUploadArray.slice(0, index),
        ...props.fileToUploadArray.slice(index + 1),
      ];
      props.setFileToUploadArray(copyFileToUploadArray);
    }
    props.formInputProps.onChange(invisible ? undefined : null);
  };

  const uploadFileJSX = (
    <Stack spacing={8}>
      <Center>
        <Image width={24} alt={""} src={"/images/cloud-upload.svg"} />
      </Center>
      <Stack spacing={0}>
        {props.inputTemplate.label ? (
          <Text lineClamp={2}>
            <div
              style={{
                ...uploadFileStyle.label,
                ...(componentStyles?.uploadFileStyle?.label || {}),
              }}
            >
              {props.inputTemplate.label}
              {props.requiredInput && (
                <span style={{ color: "red" }}>{" *"}</span>
              )}
            </div>
          </Text>
        ) : (
          <></>
        )}
        <div
          style={{
            ...uploadFileStyle.placeholder,
            ...(componentStyles?.uploadFileStyle?.placeholder || {}),
          }}
        >
          {parse(purfifyHTML(props.inputTemplate.placeholder)) || (
            <div style={{ color: "gray", fontSize: 14 }}>
              Kéo thả file ở đây
            </div>
          )}
        </div>
      </Stack>
    </Stack>
  );

  const filePreviewJSX = filePreview ? (
    <Center w="100%">
      <Group
        spacing={10}
        align="center"
        position="center"
        noWrap
        style={{ maxWidth: "100%", overflow: "hidden" }}
      >
        <FileThumbnail file={filePreview} actualHostName={actualHostName} />
        <Text lineClamp={1} ta={"center"} fz={16} style={{ minWidth: 0 }}>
          {filePreview.originalName ||
            filePreview.name ||
            "Failed to fetch filename"}
        </Text>
        <Flex gap={10} align={"center"}>
          <ActionIcon
            color="dark"
            style={{
              cursor: hasUploaded && "not-allowed",
              color: hasUploaded && "lightgray",
              backgroundColor: hasUploaded && "transparent",
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (hasUploaded) return;
              window.open(
                buildFileUrl(
                  filePreview.url || filePreview.name,
                  actualHostName,
                ),
                "_blank",
              );
            }}
          >
            <BiDownload size={"1.3rem"} />
          </ActionIcon>
          {!disabled && (
            <ActionIcon
              color="red"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleDelete();
              }}
            >
              <BiTrash size={"1.3rem"} />
            </ActionIcon>
          )}
        </Flex>
      </Group>
    </Center>
  ) : (
    // <Center>
    //   <Image width={32} alt={""} src={"/images/cloud-disabled.svg"} />
    // </Center>
    <></>
  );

  const renderDropzoneContent = () => (
    <Stack spacing={10} w={"100%"} py={10}>
      {isNil(filePreview) ? uploadFileJSX : filePreviewJSX}
    </Stack>
  );

  return (
    <Stack spacing={8}>
      <Dropzone
        openRef={openRef}
        accept={acceptedFileType}
        {...inputProps}
        disabled={disabled}
        multiple={false}
        maxSize={maxSize}
        style={{
          marginTop: 20,
          backgroundColor: disabled && "transparent",
          cursor: disabled && "not-allowed",
          border: "1px dashed lightgray",
        }}
        onReject={handleReject}
        styles={{ inner: { pointerEvents: "all" } }}
        data-form-error={inputProps.error ? "true" : undefined}
      >
        <Dropzone.Idle>{renderDropzoneContent()}</Dropzone.Idle>
        <Dropzone.Accept>{renderDropzoneContent()}</Dropzone.Accept>
        <Dropzone.Reject>{renderDropzoneContent()}</Dropzone.Reject>
      </Dropzone>
      {inputProps.error ? (
        <div
          style={{
            marginTop: -5,
            marginBottom: 10,
            color: "red",
            fontSize: 12,
          }}
        >
          {inputProps.error}
        </div>
      ) : (
        <></>
      )}
      {error && (
        <div
          style={{
            marginBottom: 10,
            color: "red",
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
    </Stack>
  );
}

function SHNBNKDropzoneAndDate(props) {
  const openRef = useRef(null);
  const [error, setError] = useState(null);
  const [maxSize, setMaxSize] = useState(5 * 1024 ** 2);
  const [filePreview, setFilePreview] = useState(null);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [oldInvisible, setOldInvisible] = useState(false);
  const [acceptedFileType, setAcceptedFileType] = useState([
    ...IMAGE_MIME_TYPE,
    ...PDF_MIME_TYPE,
  ]);
  const [dateValue, setDateValue] = useState(
    props.initialValue?.date ? new Date(props.initialValue.date) : null,
  );
  const [hasDateCustomError, setHasDateCustomError] = useState(false);

  const trans = useTrans();
  const { locale } = useRouter();
  const inputTemplate = { ...props.inputTemplate };
  const inputProps = { ...props.formInputProps };

  const disabled = props.disabledInput || inputProps.disabled;
  const actualHostName = getPortalApiBase(props.hostNameUrl);
  const descriptionJSX = getDescriptionStyleJSX(inputTemplate);

  const componentStyles = props.formInputProps.styles;

  // --- date inputProps (separate, used only by DatePicker) ---
  const dateInputProps = { ...props.formInputProps }; // <- distinct prop bag from parent
  const customDateFormat = props.customDateFormat || "DD/MM/YYYY";

  const hasFile = !isNil(filePreview);
  const dateRequired = hasFile;
  const dateDisabled =
    props.disabledInput || dateInputProps.disabled || hasFile;

  const uploadFileStyle = {
    label: {
      textAlign: "center",
      fontWeight: 700,
    },
    placeholder: {
      textAlign: "center",
    },
  };

  const handleFile = (file) => {
    if (!file) return;

    setError(null);

    const url = URL.createObjectURL(file);
    const name = file.name;
    const type = file.type;

    setHasUploaded(true);
    setFilePreview({ name, type, url });
    props.formInputProps.onChange({
      file: file,
      date: dateValue,
    });

    const index =
      props.fileToUploadArray && props.setFileToUploadArray
        ? props.fileToUploadArray.findIndex(
            (e) => e.inputName === props.inputName,
          )
        : null;

    if (index !== null) {
      let copyFileToUploadArray;

      if (index >= 0) {
        copyFileToUploadArray = [
          ...props.fileToUploadArray.slice(0, index),
          ...props.fileToUploadArray.slice(index + 1),
        ];
      } else {
        copyFileToUploadArray = [...props.fileToUploadArray];
      }

      copyFileToUploadArray.push({
        inputName: props.inputName,
        file: file,
        inputTemplate: props.inputTemplate,
      });

      props.setFileToUploadArray(copyFileToUploadArray);
    }
  };

  inputProps.onDrop = (files) => {
    if (!files || !Array.isArray(files) || files.length <= 0) return;

    handleFile(files[0]);
  };

  inputProps.onChange = (event) => {
    const files = event?.target?.files;

    if (!files || files.length === 0) return;

    const file = files[0];
    const errors = getFileRejections({ file, acceptedFileType, maxSize });

    if (errors.length > 0) {
      handleReject([{ file, errors }]);
      event.target.value = "";
      return;
    }

    handleFile(file);
  };

  const handleDateChange = (valueDate) => {
    if (valueDate) {
      const momentObject = moment(valueDate, customDateFormat, "vi");

      switch (customDateFormat) {
        case "MM/YYYY":
          momentObject.date(1);
          break;
        case "YYYY":
          momentObject.dayOfYear(1);
          break;
        default:
          break;
      }

      const isValidYear =
        momentObject.year() >= (inputTemplate.minYear || 1900) &&
        momentObject.year() <= (inputTemplate.maxYear || 2100);

      setHasDateCustomError(!isValidYear);
      props.setHasCustomError?.(!isValidYear);

      const newDate = momentObject.toDate();
      setDateValue(newDate);
      props.formInputProps.onChange({ file: filePreview, date: newDate });
    } else if (valueDate === null) {
      setDateValue(null);
      props.formInputProps.onChange({ file: filePreview, date: null });
    } else {
      // invalid typed input (undefined from dateParser)
      setHasDateCustomError(true);
      props.setHasCustomError?.(true);
      props.formInputProps.onChange({ file: filePreview, date: dateValue });
    }
  };

  const handleReject = (rejections) => {
    const firstError = rejections[0]?.errors[0]?.code;
    handleDelete();

    switch (firstError) {
      case "file-too-large":
        setError(
          `${props.inputTemplate?.errorSizeMsg?.[locale] || trans.formDynamic.file.errorSize} ${humanFileSize(maxSize, true, 0)}`,
        );
        break;

      case "file-invalid-type":
        setError(
          `${props.inputTemplate?.errorTypeMsg?.[locale] || trans.formDynamic.file.errorType}`,
        );
        break;

      case "too-many-files":
        setError(
          `${props.inputTemplate?.errorTooManyMsg?.[locale] || trans.formDynamic.file.errorTooMany}`,
        );
        break;

      default:
        setError(
          `${props.inputTemplate?.errorGenericMsg?.[locale] || trans.formDynamic.file.errorGeneric}`,
        );
    }
  };

  useEffect(() => {
    if (
      isNil(props.inputTemplate?.maxSize) ||
      typeof props.inputTemplate?.maxSize != "number"
    )
      return;
    setMaxSize(props.inputTemplate?.maxSize);
  }, [props.inputTemplate?.maxSize]);

  useEffect(() => {
    let temp = props.inputTemplate?.acceptedFileType;
    if (isNil(temp) || !Array.isArray(temp) || temp.length == 0) return;

    setAcceptedFileType(temp);
  }, [props.inputTemplate?.acceptedFileType]);

  useEffect(() => {
    if (hasUploaded) {
      setHasUploaded(false);
    }
  }, [props.isFormSubmitted]);

  useEffect(() => {
    setFilePreview(props.initialValue?.file || null);
    setDateValue(
      props.initialValue?.date ? new Date(props.initialValue.date) : null,
    );
  }, [props.initialValue]);

  useEffect(() => {
    if (props.inputTemplate.invisible) {
      setOldInvisible(true);
      handleDelete(true);
    } else if (oldInvisible) {
      setOldInvisible(false);
      setFilePreview(null);
      inputProps.onChange(
        props.initialValue === undefined ? null : props.initialValue,
      );
    }
  }, [props.inputTemplate.invisible]);

  const handleDelete = (invisible = false) => {
    setFilePreview(invisible ? undefined : null);
    const index =
      props.fileToUploadArray && props.setFileToUploadArray
        ? props.fileToUploadArray.findIndex(
            (e) => e.inputName === props.inputName,
          )
        : null;

    if (index !== null && index >= 0) {
      const copyFileToUploadArray = [
        ...props.fileToUploadArray.slice(0, index),
        ...props.fileToUploadArray.slice(index + 1),
      ];
      props.setFileToUploadArray(copyFileToUploadArray);
    }
    props.formInputProps.onChange({ file: null, date: dateValue });
  };

  const uploadFileJSX = (
    <Stack spacing={8}>
      <Center>
        <Image width={24} alt={""} src={"/images/cloud-upload.svg"} />
      </Center>
      <Stack spacing={0}>
        {inputTemplate.label ? (
          <Text lineClamp={2}>
            <div
              style={{
                ...uploadFileStyle.label,
                ...(componentStyles?.uploadFileStyle?.label || {}),
              }}
            >
              {inputTemplate.label}
              {props.requiredInput && (
                <span style={{ color: "red" }}>{" *"}</span>
              )}
            </div>
          </Text>
        ) : (
          <></>
        )}
        <div
          style={{
            ...uploadFileStyle.placeholder,
            ...(componentStyles?.uploadFileStyle?.placeholder || {}),
          }}
        >
          {parse(purfifyHTML(inputTemplate.placeholder)) || (
            <div style={{ color: "gray", fontSize: 14 }}>
              Kéo thả file ở đây
            </div>
          )}
        </div>
      </Stack>
    </Stack>
  );

  const filePreviewJSX = filePreview ? (
    <Center w="100%">
      <Group
        spacing={10}
        align="center"
        position="center"
        noWrap
        style={{ maxWidth: "100%", overflow: "hidden" }}
      >
        <FileThumbnail file={filePreview} actualHostName={actualHostName} />
        <Text lineClamp={1} ta={"center"} fz={16} style={{ minWidth: 0 }}>
          {filePreview.originalName ||
            filePreview.name ||
            "Failed to fetch filename"}
        </Text>
        <Flex gap={10} align={"center"}>
          <ActionIcon
            color="dark"
            style={{
              cursor: hasUploaded && "not-allowed",
              color: hasUploaded && "lightgray",
              backgroundColor: hasUploaded && "transparent",
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (hasUploaded) return;
              window.open(
                buildFileUrl(
                  filePreview.url || filePreview.name,
                  actualHostName,
                ),
                "_blank",
              );
            }}
          >
            <BiDownload size={"1.3rem"} />
          </ActionIcon>
          {!disabled && (
            <ActionIcon
              color="red"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleDelete();
              }}
            >
              <BiTrash size={"1.3rem"} />
            </ActionIcon>
          )}
        </Flex>
      </Group>
    </Center>
  ) : (
    // <Center>
    //   <Image width={32} alt={""} src={"/images/cloud-disabled.svg"} />
    // </Center>
    <></>
  );

  const renderDropzoneContent = () => (
    <Stack spacing={10} w={"100%"} py={10}>
      {isNil(filePreview) ? uploadFileJSX : filePreviewJSX}
    </Stack>
  );
  return (
    <Stack w={"100%"} spacing={8} mt={20}>
      <Grid w={"100%"}>
        <Grid.Col w={"100%"} span={12} sm={8}>
          <Dropzone
            w={"100%"}
            openRef={openRef}
            accept={acceptedFileType}
            {...inputProps}
            disabled={disabled}
            multiple={false}
            maxSize={maxSize}
            style={{
              backgroundColor: disabled && "transparent",
              cursor: disabled && "not-allowed",
              border: "1px dashed lightgray",
            }}
            onReject={handleReject}
            styles={{ inner: { pointerEvents: "all" } }}
            data-form-error={inputProps.error ? "true" : undefined}
          >
            <Dropzone.Idle>{renderDropzoneContent()}</Dropzone.Idle>
            <Dropzone.Accept>{renderDropzoneContent()}</Dropzone.Accept>
            <Dropzone.Reject>{renderDropzoneContent()}</Dropzone.Reject>
          </Dropzone>
        </Grid.Col>
        <Grid.Col w={"100%"} span={12} sm={4}>
          <Flex w={"100%"} h={"100%"} align={"center"}>
            <DatePicker
              w={"100%"}
              className={`${styles.formDynamicTextDateSelectInput} formDynamicTextDateSelectInput`}
              allowFreeInput={true}
              locale="vi"
              value={dateValue}
              onChange={handleDateChange}
              dateParser={(value) => {
                if (hasDateCustomError) {
                  setHasDateCustomError(false);
                  props.setHasCustomError?.(false);
                }
                if (value.length === 0) {
                  return null;
                }
                const momentObject = moment(value, customDateFormat, "vi");
                if (momentObject.isValid()) {
                  if (
                    momentObject.year() < (inputTemplate.minYear || 1900) ||
                    momentObject.year() > (inputTemplate.maxYear || 2100)
                  ) {
                    setHasDateCustomError(true);
                    props.setHasCustomError?.(true);
                    return momentObject.toDate();
                  }
                  return momentObject.toDate();
                } else {
                  return undefined;
                }
              }}
              label={
                inputTemplate.labelNoWrap ? (
                  <span style={{ textWrap: "nowrap" }}>
                    {inputTemplate.dateLabel}
                  </span>
                ) : (
                  inputTemplate.dateLabel
                )
              }
              dropdownPosition={"bottom-start"}
              withAsterisk={!dateRequired}
              inputFormat={customDateFormat}
              placeholder={
                inputTemplate.datePlaceholder
                  ? inputTemplate.datePlaceholder
                  : inputTemplate.dateLabel
              }
              disabled={dateDisabled}
              minDate={inputTemplate.minToday && dayjs(new Date()).toDate()}
              maxDate={inputTemplate.maxToday && dayjs(new Date()).toDate()}
            />
          </Flex>
        </Grid.Col>
      </Grid>

      {inputProps.error ? (
        <div
          style={{
            marginTop: -5,
            marginBottom: 10,
            color: "red",
            fontSize: 12,
          }}
        >
          {inputProps.error}
        </div>
      ) : (
        <></>
      )}
      {error && (
        <div
          style={{
            marginBottom: 10,
            color: "red",
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
    </Stack>
  );
}

export default FormDynamicInputDropzone;
